import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Nachricht gesendet.");
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      toast.error(
        err?.response?.status === 429
          ? "Zu viele Anfragen. Bitte versuche es in ein paar Minuten erneut."
          : "Etwas ist schiefgelaufen. Bitte versuche es erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="support" data-testid="support-section" className="border-b border-white/10">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-5 py-20 lg:grid-cols-[1fr_1.2fr] lg:gap-20 lg:px-8 lg:py-24">
        <Reveal>
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#8B5CF6]">Kontaktformular</p>
          <h2 className="font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl">
            Schreib uns.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-white/60">
            Fragen, Feedback oder Bock auf die Beta? Wir freuen uns über jede Nachricht.
          </p>
          <a
            href="mailto:support@festivalplan.app"
            data-testid="support-email-link"
            className="mt-8 inline-flex items-center gap-3 text-white transition-colors hover:text-[#00E5FF]"
          >
            <Mail className="h-5 w-5 text-[#00E5FF]" />
            support@festivalplan.app
          </a>
        </Reveal>

        <Reveal delay={0.05}>
          <form onSubmit={submit} data-testid="contact-form" className="relative rounded-2xl border border-white/10 bg-[#241e19]/70 p-6 sm:p-8">
            <div className="space-y-5">
              <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                  data-testid="contact-honeypot"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-white/60">Name</label>
                <Input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Dein Name"
                  className="h-12 rounded-xl border-white/10 bg-black/40 text-white placeholder:text-white/30 focus-visible:border-[#8B5CF6] focus-visible:ring-0"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-white/60">E-Mail</label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="du@beispiel.de"
                  className="h-12 rounded-xl border-white/10 bg-black/40 text-white placeholder:text-white/30 focus-visible:border-[#8B5CF6] focus-visible:ring-0"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-white/60">Nachricht</label>
                <Textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Wie können wir helfen?"
                  rows={5}
                  className="rounded-xl border-white/10 bg-black/40 text-white placeholder:text-white/30 focus-visible:border-[#8B5CF6] focus-visible:ring-0"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-primary flex w-full items-center justify-center gap-2 py-3.5 text-sm font-medium disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Nachricht senden
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
